import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Button, CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import api from '../api/api';
import SignaturePad from '../components/SignaturePad';
import { useAuth } from '../context/AuthContext';

const MySignature: React.FC = () => {
  const { user } = useAuth();
  const [signature, setSignature] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success'|'error'}>({open: false, message: '', severity: 'success'});

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api.get('/signature/get-signature', { params: { user_id: user.id, role: user.role }, responseType: 'blob' })
      .then(r => {
        setSignature(URL.createObjectURL(r.data as Blob));
        setLoading(false);
      })
      .catch(() => {
        setSignature(null);
        setLoading(false);
      });
  }, [user]);

  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  const handleSignatureSave = async (dataUrl: string) => {
    if (!user) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('user_id', String(user.id));
      formData.append('role', user.role);
      formData.append('image_data', dataUrl);
      await api.post('/signature/upload-signature', formData);
      setSnackbar({open: true, message: 'Signature uploaded successfully', severity: 'success'});
      // Refresh signature
      api.get('/signature/get-signature', { params: { user_id: user.id, role: user.role }, responseType: 'blob' })
        .then(r => setSignature(URL.createObjectURL(r.data as Blob)));
      handleCloseDialog();
    } catch {
      setSnackbar({open: true, message: 'Failed to upload signature', severity: 'error'});
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
      <Typography variant="h4" gutterBottom>My Signature</Typography>
      <Typography variant="body1" gutterBottom>
        You can upload or update your digital signature below. This will be used for official documents and reports.
      </Typography>
      <Paper sx={{ mt: 3, p: 3, textAlign: 'center' }}>
        <Box sx={{ mb: 2 }}>
          {signature ? <img src={signature} alt="Signature" style={{ maxHeight: 60, background: '#fff', border: '1px solid #ccc' }} /> : <em>No signature uploaded.</em>}
        </Box>
        <Button variant="contained" onClick={handleOpenDialog}>
          {signature ? 'Update Signature' : 'Upload Signature'}
        </Button>
      </Paper>
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Your Signature</DialogTitle>
        <DialogContent>
          <SignaturePad onSave={handleSignatureSave} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={uploading}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({...s, open: false}))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({...s, open: false}))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MySignature; 